import { useNavigate } from "@tanstack/react-router";

import { Link } from "@/components/link";
import { Switch } from "@/components/switch";
import { toast } from "@/components/toast/toast-queue";
import { FeaturePageLayout } from "@/features/shared/layout";

import { GuidelineAssignment } from "../GuidelineAssignment";
import { ReadOnlyField } from "../ReadOnlyField";
import { useMbcAdminData } from "../useMbcAdminData";
import { useSetDesignTypeMbc } from "./hooks/useSetDesignTypeMbc";

export function DesignTypePage({
  designTypeId,
}: {
  readonly designTypeId: number;
}) {
  const navigate = useNavigate();
  const setDesignTypeMbc = useSetDesignTypeMbc();
  const { assignments, catalog, error, guidelines, isLoading } =
    useMbcAdminData();

  const subCategory = catalog?.subCategories.find(({ designTypes }) =>
    designTypes.some(({ id }) => id === designTypeId),
  );
  const designType = [
    ...(catalog?.subCategories ?? []).flatMap(({ designTypes }) => designTypes),
    ...(catalog?.uncategorizedDesignTypes ?? []),
  ].find(({ id }) => id === designTypeId);

  return (
    <div className="p-xl">
      <FeaturePageLayout
        backButton
        isLoading={isLoading}
        title="Edit design type"
      >
        <div className="space-y-8 rounded-b-xl bg-bg-primary px-6 py-8">
          {error && (
            <p className="text-sm text-text-error" role="alert">
              Could not load the design type: {error.message}
            </p>
          )}
          {!designType && !error && (
            <p className="text-sm text-text-tertiary">Design type not found</p>
          )}
          {designType && assignments && guidelines && (
            <>
              <dl className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <ReadOnlyField label="ID">{designType.id}</ReadOnlyField>
                <ReadOnlyField label="Name">{designType.name}</ReadOnlyField>
                <ReadOnlyField label="Subcategory">
                  {subCategory ? (
                    <Link
                      className="text-sm"
                      href={`/admin/subcategories/${subCategory.id}`}
                    >
                      {subCategory.name}
                    </Link>
                  ) : (
                    "—"
                  )}
                </ReadOnlyField>
              </dl>

              <div className="rounded-lg border border-border-primary px-4 py-3">
                <Switch
                  isDisabled={setDesignTypeMbc.isPending}
                  isSelected={designType.isMbcCompatible}
                  onChange={(enabled) =>
                    setDesignTypeMbc.mutate(
                      { designTypeId, enabled },
                      {
                        onError: (mutationError) =>
                          toast.add({
                            description: mutationError.message,
                            title: "Could not update the design type",
                            type: "error",
                          }),
                      },
                    )
                  }
                >
                  MBC enabled
                </Switch>
              </div>

              <GuidelineAssignment
                assignments={assignments}
                guidelines={guidelines}
                onClose={() => navigate({ to: "/admin/design-types" })}
                scope={{
                  designTypeId,
                  kind: "designType",
                  name: designType.name,
                  subCategoryId: designType.subCategoryId,
                }}
                subCategoryName={subCategory?.name}
              />
            </>
          )}
        </div>
      </FeaturePageLayout>
    </div>
  );
}
